import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { FaUpload, FaTrash } from "react-icons/fa";
import useFileUploadMutation from "../../../Gifts/Logos/hooks/useFileUploadMutation";
import DSBox from "../../../system-design/DSBox";
import DSTypography from "../../../system-design/DSTypography";
import DSInput from "../../../system-design/DSInput";
import DSCard from "../../../system-design/DSCard";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../system-design/sectionCardSx";
import DSCardContent from "../../../system-design/DSCardContent";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSSwitch from "../../../system-design/DSSwitch";
import useKlydoMutation from "../../hooks/useKlydoMutation";
import type { PendulumLogoType } from "../../../../api/klydo";

interface PendulumCardProps {
  pendulumLogoUrl?: string | null;
  pendulumLogoHoldStatus?: boolean;
  pendulumLogoType?: PendulumLogoType;
  klydoId: string;
}

const PendulumCard: React.FC<PendulumCardProps> = ({
  pendulumLogoUrl,
  klydoId,
  pendulumLogoHoldStatus,
  pendulumLogoType,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // The type the NEXT upload is baked for. It cannot change while a logo
  // exists, because the stored image was baked for the old type.
  const [fullBob, setFullBob] = useState(pendulumLogoType === "full");
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    pendulumLogoUrl || null,
  );

  const showError = (error: unknown) => {
    const axiosMessage = (
      error as { response?: { data?: { message?: string } } }
    )?.response?.data?.message;
    const text =
      axiosMessage ||
      (error instanceof Error ? error.message : "Unknown error");
    setErrorMessage(text);
    setTimeout(() => setErrorMessage(""), 5000);
  };

  const { mutate: updateKlydoHoldStatus, isPending: isUpdatingHoldStatus } =
    useKlydoMutation(klydoId, {
      onError: showError,
    });

  const { mutate: deleteKlydoPendulumLogo, isPending: isDeletingPendulumLogo } =
    useKlydoMutation(klydoId, {
      onSuccess: () => {
        updateKlydoHoldStatus({ holdPendulumLogo: false });
      },
      onError: (error) => {
        showError(error);
        setPreviewUrl(pendulumLogoUrl || null);
      },
    });

  const { mutate: updateKlydoLogo, isPending: isUpdatingLogo } =
    useKlydoMutation(klydoId, {
      onSuccess: (data, variables) => {
        setPreviewUrl(variables.pendulumLogo as string);
        updateKlydoHoldStatus({ holdPendulumLogo: false });
      },
      onError: (error) => {
        showError(error);
        setPreviewUrl(pendulumLogoUrl || null);
      },
    });

  const { mutate: uploadFile, isPending: isUploadingFile } =
    useFileUploadMutation({
      onSuccess: (uploadResponse) => {
        updateKlydoLogo({
          pendulumLogo: uploadResponse.url,
          pendulumLogoType: fullBob ? "full" : "sized",
        });
        setPreviewUrl(pendulumLogoUrl || null);
      },
      onError: (error) => {
        showError(error);
        setPreviewUrl(pendulumLogoUrl || null);
      },
    });

  const isLoading =
    isDeletingPendulumLogo ||
    isUpdatingHoldStatus ||
    isUpdatingLogo ||
    isUploadingFile;

  useEffect(() => {
    setPreviewUrl(pendulumLogoUrl || null);
  }, [pendulumLogoUrl]);

  useEffect(() => {
    setFullBob(pendulumLogoType === "full");
  }, [pendulumLogoType]);

  const handleDeletePendulumLogo = () => {
    setPreviewUrl(null);
    deleteKlydoPendulumLogo({
      pendulumLogo: null,
      pendulumLogoType: "sized",
    });
  };

  const handleHoldPendulumLogo = (checked: boolean) => {
    updateKlydoHoldStatus({ holdPendulumLogo: checked });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select an image file");
      return;
    }

    setErrorMessage("");
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setPreviewUrl(base64);
      uploadFile({
        file: base64,
        pendulumLogoType: fullBob ? "full" : "sized",
      });
    };

    reader.onerror = () => {
      setErrorMessage("Failed to process file");
      setPreviewUrl(pendulumLogoUrl || null);
    };

    reader.readAsDataURL(file);
  };

  return (
    <DSCard className="card-stats" sx={sectionCardSx}>
      <DSSectionHeading>Pendulum Logo</DSSectionHeading>
      <DSCardContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {previewUrl && (
            <DSBox sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <DSBox
                sx={{
                  width: "150px",
                  height: "150px",
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "background.paper",
                }}
              >
                <img
                  src={previewUrl}
                  alt="Pendulum Logo Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "4px",
                  }}
                />
              </DSBox>
            </DSBox>
          )}

          <DSBox sx={{ textAlign: "center" }}>
            {errorMessage && (
              <DSTypography sx={{ color: "error.main", fontSize: "14px" }}>
                {errorMessage}
              </DSTypography>
            )}
          </DSBox>

          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <DSSwitch
              checked={fullBob}
              onChange={(event) => setFullBob(event.target.checked)}
              disabled={isLoading || Boolean(pendulumLogoUrl)}
              color="primary"
            />
            <DSTypography variant="body2">
              Full bob (fills the whole pendulum, not tinted). Use a square PNG,
              at least 410×410px: smaller images are enlarged and non-square
              ones are cropped.
              {pendulumLogoUrl ? " Delete the logo to change this." : ""}
            </DSTypography>
          </DSBox>

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "center",
              }}
            >
              <DSInput
                inputProps={{
                  accept: "image/*",
                  style: { display: "none" },
                  ref: fileInputRef,
                }}
                type="file"
                onChange={handleFileChange}
                disabled={isLoading}
                id="pendulum-upload-input"
              />
              <DSPrimaryButton
                disabled={isLoading}
                onClick={() => {
                  if (!isLoading && fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
                sx={{
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              >
                <FaUpload style={{ marginRight: "8px" }} />
                {isLoading ? "Processing..." : "Choose Pendulum Logo"}
              </DSPrimaryButton>

              {pendulumLogoUrl && (
                <DSSecondaryButton
                  onClick={handleDeletePendulumLogo}
                  disabled={isLoading}
                >
                  <FaTrash style={{ marginRight: "8px" }} />
                  Delete Logo
                </DSSecondaryButton>
              )}
            </DSBox>
          </DSBox>

          {pendulumLogoUrl && (
            <DSBox
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                alignContent: "center",
              }}
            >
              <DSSwitch
                checked={pendulumLogoHoldStatus || false}
                onChange={(event) =>
                  handleHoldPendulumLogo(event.target.checked)
                }
                color="primary"
              />
              <DSTypography variant="body2">Hold pendulum logo</DSTypography>
            </DSBox>
          )}
        </DSBox>
      </DSCardContent>
    </DSCard>
  );
};

export default PendulumCard;
